import { useContext, useEffect, useMemo, useState } from 'react'
import TablaCajaPrincipal from './TablaCajaPrincipal'
import GlobalContext from '../../../../../../../../context/global-context'
import {
  currentMonth,
  currentYear,
  monthsOptions as months,
  yearsOptions
} from '../../../../../../../utils/constant'
import { BusinessAPI } from '../../../../../../../../api/business.api'
import { Select } from '../../../../../../ui'
import LoaderAdm from '../../../../../../../../assets/svgIcons/LoaderAdm.json'
import Lottie from 'lottie-react'
import CustomPagination from '../../../../../../table/CustomPagination'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import clsx from 'clsx'
import SearchFilter from '../../../../../../ui/SearchFilter'
import { getDocumentSourceDate } from './massiveLinkageDebug'

const monthNames = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre'
]

const normalizeText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()

const isCreditNoteName = value => normalizeText(value).includes('NOTA DE CREDITO')
const normalizeStatus = value => normalizeText(value).replace(/_/g, ' ').trim()
const UNDATED_BUCKET_KEY = '__sin_fecha__'

const getStatusKey = status => {
  const normalized = normalizeStatus(status)

  if (!normalized) return 'UNKNOWN'
  if (normalized === 'PENDIENTE') return 'PENDIENTE'
  if (normalized.includes('PARCIAL')) {
    if (normalized.includes('PAG')) return 'PARCIAL_PAGADO'
    if (normalized.includes('COBR')) return 'PARCIAL_COBRADO'
    if (normalized.includes('CONCILI')) return 'PARCIAL_CONCILIADO'
  }
  if (normalized.includes('POR PAG')) return 'POR_PAGAR'
  if (normalized.includes('POR COB')) return 'POR_COBRAR'
  if (normalized.includes('PAGADO')) return 'PAGADO'
  if (normalized.includes('COBRADO')) return 'COBRADO'
  if (normalized.includes('CONCILIADO')) return 'CONCILIADO'

  return normalized
}

const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getLinkedEntryAmount = item =>
  Math.abs(
    toNumber(
      item?.montoRelacionado ??
      item?.montoAbono ??
      item?.montoCargo ??
      item?.montoTotal ??
      item?.amount
    )
  )

const getDocumentBaseAmount = item => {
  const isHonorarySlip =
    item?.nombreCuenta === 'Boletas de Honorarios' || item?.nombreFolio === 'Boleta'

  return toNumber(isHonorarySlip ? item?.montoNetoLiquido : item?.montoTotal)
}

const getExplicitPendingAmount = item => {
  const candidates = [
    item?.saldoPendiente,
    item?.montoPendiente,
    item?.saldoPorCobrar,
    item?.saldoPorPagar,
    item?.remainingAmount,
    item?.pendingAmount
  ]

  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue
    const amount = toNumber(candidate)
    if (amount > 0) return amount
  }

  return null
}

const getLinkedAmount = item => {
  if (Array.isArray(item?.linkage)) {
    return item.linkage.reduce((acc, entry) => acc + getLinkedEntryAmount(entry), 0)
  }

  if (item?.linkage) {
    return getLinkedEntryAmount(item.linkage)
  }

  if (Array.isArray(item?.auxiliar)) {
    return item.auxiliar.reduce((acc, entry) => acc + getLinkedEntryAmount(entry), 0)
  }

  return 0
}

const getSelectableAmount = item => {
  const explicitPending = getExplicitPendingAmount(item)
  if (explicitPending !== null) return explicitPending

  const baseAmount = getDocumentBaseAmount(item)
  const linkedAmount = getLinkedAmount(item)
  const statusKey = getStatusKey(item?.status)

  if (linkedAmount > 0 || normalizeStatus(item?.status).includes('PARCIAL')) {
    const calculatedPending = Math.max(baseAmount - linkedAmount, 0)

    if (
      calculatedPending === 0 &&
      baseAmount > 0 &&
      ['POR_COBRAR', 'POR_PAGAR', 'PENDIENTE'].includes(statusKey)
    ) {
      return baseAmount
    }

    return calculatedPending
  }

  return baseAmount
}

const ListCaja = ({
  type,
  onSeleccionar,
  filasSeleccionadas,
  setRowsSeleccionables,
  onFilterContextChange
}) => {
  const { currentBusiness } = useContext(GlobalContext)
  const [data, setData] = useState({})
  const [monthFilter, setMonthFilter] = useState(0)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const businessAPI = useMemo(() => new BusinessAPI(), [])
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [documentFilter, setDocumentFilter] = useState('')
  const [highlightedRows] = useState({})
  const [selectedFilter] = useState('')
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [page, setPage] = useState(0)
  const [showFilters, setShowFilters] = useState(true)
  const [filterValues, setFilterValues] = useState({
    montoDesde: '',
    montoHasta: '',
    descripcion: ''
  })
  const [order, setOrder] = useState({
    col: '',
    field: '',
    direction: 'asc'
  })

  const selectedMonth = Number(monthFilter) || 0
  const selectedYear = Number(yearFilter) || currentYear
  const isCurrentYear = selectedYear === currentYear

  useEffect(() => {
    if (isCurrentYear && selectedMonth > currentMonth) {
      setMonthFilter(0)
    }
  }, [isCurrentYear, selectedMonth])

  useEffect(() => {
    if (!currentBusiness?.id) return

    setIsLoadingData(true)

    businessAPI
      .getListSummaryMovementsSpecific({
        businessId: currentBusiness.id,
        month: 0,
        year: selectedYear,
        code: [],
        linkage: true,
        max: 10000,
        type
      })
      .then(res => {
        const movs = res.data?.libros ?? res.data ?? []

        setData({
          Ingreso: type === 'ingreso' ? movs : [],
          Egreso: type === 'egreso' ? movs : []
        })
      })
      .catch(console.error)
      .finally(() => setIsLoadingData(false))
  }, [businessAPI, currentBusiness?.id, selectedYear, type])

  const monthsOptions = useMemo(
    () => [
      { value: 0, label: 'Todos los meses' },
      ...months.map(opt => ({
        ...opt,
        disabled: isCurrentYear && opt.value > currentMonth
      }))
    ],
    [isCurrentYear]
  )

  const dataPorMes = useMemo(() => {
    const resultado = {}

    const agregar = (mov, movimientoTipo) => {
      const fecha = getDocumentSourceDate(mov)
      if (!fecha) {
        if (!resultado[UNDATED_BUCKET_KEY]) {
          resultado[UNDATED_BUCKET_KEY] = { ingreso: [], egreso: [] }
        }

        if (movimientoTipo === 'ingreso') resultado[UNDATED_BUCKET_KEY].ingreso.push(mov)
        if (movimientoTipo === 'egreso') resultado[UNDATED_BUCKET_KEY].egreso.push(mov)
        return
      }

      const date = new Date(fecha)
      if (Number.isNaN(date.getTime())) {
        if (!resultado[UNDATED_BUCKET_KEY]) {
          resultado[UNDATED_BUCKET_KEY] = { ingreso: [], egreso: [] }
        }

        if (movimientoTipo === 'ingreso') resultado[UNDATED_BUCKET_KEY].ingreso.push(mov)
        if (movimientoTipo === 'egreso') resultado[UNDATED_BUCKET_KEY].egreso.push(mov)
        return
      }

      const mesIndex = date.getUTCMonth()
      const mesNombre = monthNames[mesIndex]

      if (!resultado[mesNombre]) {
        resultado[mesNombre] = { ingreso: [], egreso: [] }
      }

      if (movimientoTipo === 'ingreso') resultado[mesNombre].ingreso.push(mov)
      if (movimientoTipo === 'egreso') resultado[mesNombre].egreso.push(mov)
    }

    ;(data.Ingreso ?? []).forEach(mov => agregar(mov, 'ingreso'))
    ;(data.Egreso ?? []).forEach(mov => agregar(mov, 'egreso'))

    return resultado
  }, [data])

  const rowsForTable = useMemo(() => {
    if (selectedMonth) {
      const idx = selectedMonth - 1
      const mes = dataPorMes[monthNames[idx]]
      if (!mes) return []
      return [...mes.ingreso, ...mes.egreso]
    }

    return Object.values(dataPorMes).flatMap(m => [...m.ingreso, ...m.egreso])
  }, [dataPorMes, selectedMonth])

  const filtroDocumento = useMemo(() => {
    const cleanedRows = rowsForTable.filter(
      i =>
        !isCreditNoteName(i.nombreFolio) &&
        !normalizeStatus(i.status).includes('ANULA')
    )

    const nombres = cleanedRows.map(i => i.nombreFolio || i.nombreCuenta)

    return [
      { value: '', label: 'Todos los documentos' },
      ...[...new Set(nombres)].map(name => ({ label: name, value: name }))
    ]
  }, [rowsForTable])

  const filteredData = useMemo(() => {
    let result = rowsForTable
    const statusPermitidos =
      type === 'ingreso'
        ? ['POR_COBRAR', 'PARCIAL_COBRADO', 'PARCIAL_CONCILIADO', 'PENDIENTE']
        : ['POR_PAGAR', 'PARCIAL_PAGADO', 'PARCIAL_CONCILIADO', 'PENDIENTE']

    result = result.filter(
      i =>
        !isCreditNoteName(i.nombreFolio) &&
        !normalizeStatus(i.status).includes('ANULA')
    )

    result = result.filter(r => statusPermitidos.includes(getStatusKey(r.status)))

    result = result.map(i => {
      const montoDocumentoBase = getDocumentBaseAmount(i)
      const montoSeleccionable = getSelectableAmount(i)

      return {
        ...i,
        montoDocumentoBase,
        montoSeleccionable
      }
    })

    result = result.filter(i => i.montoSeleccionable > 0)

    if (documentFilter) {
      result = result.filter(i => (i.nombreFolio || i.nombreCuenta) === documentFilter)
    }

    const { descripcion, montoDesde, montoHasta } = filterValues

    if (descripcion) {
      const term = descripcion.toLowerCase()
      result = result.filter(
        i =>
          (i.nombreCuenta || '').toLowerCase().includes(term) ||
          (i.razonSocial || '').toLowerCase().includes(term) ||
          (i.nombreFolio || '').toLowerCase().includes(term) ||
          (i.rut || '').toLowerCase().includes(term) ||
          i.montoSeleccionable?.toString().includes(term)
      )
    }

    if (montoDesde) result = result.filter(i => i.montoSeleccionable >= +montoDesde)
    if (montoHasta) result = result.filter(i => i.montoSeleccionable <= +montoHasta)

    return result
  }, [rowsForTable, documentFilter, filterValues, selectedFilter, highlightedRows, type])

  const handleFilterChange = (k, v) => setFilterValues(prev => ({ ...prev, [k]: v }))

  const handleChangePage = p => setPage(p)
  const handleChangeRowsPerPage = r => {
    setRowsPerPage(r)
    setPage(0)
  }

  const handleSort = ({ col, field }) => {
    const asc = order.col === col && order.direction === 'asc'
    setOrder({ col, field, direction: asc ? 'desc' : 'asc' })
  }

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const A = a[order.field]
      const B = b[order.field]
      if (A == null) return 1
      if (B == null) return -1
      return order.direction === 'asc'
        ? A < B
          ? -1
          : A > B
            ? 1
            : 0
        : A > B
          ? -1
          : A < B
            ? 1
            : 0
    })
  }, [filteredData, order])

  const paginatedData =
    rowsPerPage > 0
      ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedData

  const handleToggleSeleccionarTodos = () => {
    const allVisibleRowsSelected =
      filteredData.length > 0 && filteredData.every(row => filasSeleccionadas.includes(row.id))

    if (allVisibleRowsSelected) {
      onSeleccionar('DESELECCIONAR_TODOS', filteredData)
    } else {
      onSeleccionar('SELECCIONAR_TODOS', filteredData)
    }
  }

  useEffect(() => {
    setRowsSeleccionables?.(previousRows => {
      const rowsById = new Map(previousRows.map(row => [row.id, row]))
      filteredData.forEach(row => rowsById.set(row.id, row))
      return [...rowsById.values()]
    })
  }, [filteredData, setRowsSeleccionables])

  useEffect(() => {
    onFilterContextChange?.({
      monthFilter: selectedMonth,
      yearFilter: selectedYear,
      monthLabel: selectedMonth ? monthNames[selectedMonth - 1] : 'todos'
    })
  }, [onFilterContextChange, selectedMonth, selectedYear])

  return (
    <div>
      <div className=''>
        {showFilters && (
          <div className='flex flex-wrap mt-1 gap-2 items-center border border-gray-200 rounded-3xl p-4 bg-gray-50 mb-4'>
            <Select
              options={monthsOptions}
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
            />
            <Select
              options={yearsOptions}
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value)}
            />
            <Select
              options={filtroDocumento}
              value={documentFilter}
              onChange={e => setDocumentFilter(e.target.value)}
            />
            <SearchFilter
              value={filterValues.descripcion}
              onChange={e => handleFilterChange('descripcion', e.target.value)}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
            />
            <div className='flex items-center gap-2'>
              <input
                type='checkbox'
                className='rounded'
                checked={
                  filteredData.length > 0 &&
                  filteredData.every(row => filasSeleccionadas.includes(row.id))
                }
                onChange={handleToggleSeleccionarTodos}
              />
              <label className='text-sm'>
                {filteredData.length > 0 &&
                filteredData.every(row => filasSeleccionadas.includes(row.id))
                  ? 'Deseleccionar todo'
                  : 'Seleccionar todo'}
              </label>
            </div>
            <button
              className='ml-auto flex items-center px-2 py-1 bg-gray-200 rounded hover:bg-gray-300'
              onClick={() => setShowFilters(f => !f)}
            >
              <KeyboardArrowDownIcon
                className={clsx('transition-transform', { 'rotate-180': showFilters })}
              />
            </button>
            {/*{selectedMonth === 0 && (
              <div className='w-full text-xs font-medium text-amber-700'>
                Estás viendo documentos de todos los meses. Selecciona un mes específico para
                habilitar la vinculación.
              </div>
            )}*/}
          </div>
        )}
        {!showFilters && (
          <div className='flex justify-end mb-4'>
            <button
              className='flex items-center px-2 py-1 bg-gray-200 rounded hover:bg-gray-300'
              onClick={() => setShowFilters(f => !f)}
            >
              <KeyboardArrowDownIcon
                className={clsx('transition-transform', { 'rotate-180': showFilters })}
              />
            </button>
          </div>
        )}
        {isLoadingData ? (
          <div className='flex items-center justify-center h-full'>
            <Lottie animationData={LoaderAdm} loop style={{ width: 300, height: 300 }} />
          </div>
        ) : (
          <div className='overflow-x-auto pt-4'>
            <TablaCajaPrincipal
              rows={filteredData}
              onSort={handleSort}
              order={order}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              onSeleccionar={onSeleccionar}
              filasSeleccionadas={filasSeleccionadas}
            />
          </div>
        )}
        {paginatedData.length > 0 && (
          <div className='mt-4'>
            <CustomPagination
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ListCaja
